<template>
  <div>
    <!-- Une liste avec recherche : taper filtre, seul un choix explicite confie la pose. -->
    <v-autocomplete
        v-if="mayManageOnboarding"
        v-model="form.technicianId"
        :items="technicianChoices" item-text="name" item-value="id"
        :label="$t('onboarding.commissioning.technician')"
        :disabled="!editable"
        :hint="technicianList.length ? $t('onboarding.commissioning.technician-hint') : $t('onboarding.commissioning.no-technician')"
        persistent-hint outlined dense clearable class="mb-3"
    />
    <div v-else-if="order.technician" class="mb-3">{{ $t('onboarding.commissioning.technician') }} : {{ order.technician.name }}</div>

    <v-text-field
        v-model="form.scheduledAt" type="datetime-local"
        :label="$t('onboarding.commissioning.scheduled-at')"
        :hint="$t(order.status === 'open' ? 'onboarding.commissioning.schedule-hint' : 'onboarding.commissioning.schedule-after-approval')"
        persistent-hint outlined dense :disabled="!editable" class="mb-3"
    />
    <div v-if="order.desiredDate" class="text-caption text--secondary mb-3">
      {{ $t('onboarding.commissioning.desired-date') }} : {{ dayOf(order.desiredDate) }}
    </div>

    <!-- Ce que le technicien lira sur place : qui appeler, et les consignes. -->
    <div class="text-subtitle-2 mb-2">{{ $t('onboarding.commissioning.site.title') }}</div>
    <v-row dense>
      <v-col cols="12" sm="6">
        <v-text-field v-model="form.contactName" :label="$t('onboarding.commissioning.site.contact-name')" prepend-inner-icon="mdi-account-outline" outlined dense hide-details :disabled="!editable" />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field v-model="form.contactPhone" :label="$t('onboarding.commissioning.site.contact-phone')" prepend-inner-icon="mdi-phone-outline" outlined dense hide-details :disabled="!editable" />
      </v-col>
      <v-col cols="12">
        <v-textarea v-model="form.notes" :label="$t('onboarding.commissioning.site.notes')" :hint="$t('onboarding.commissioning.site.notes-hint')"
                    persistent-hint rows="2" auto-grow outlined dense :disabled="!editable" />
      </v-col>
    </v-row>

    <div class="d-flex align-center mt-2" style="gap: 6px">
      <slot name="actions" />
      <v-spacer />
      <v-btn small text :disabled="!changed || working" @click="reset">{{ $t('cancel') }}</v-btn>
      <v-btn small color="primary" :loading="working" :disabled="!changed || !editable" @click="save">{{ $t('save') }}</v-btn>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'

/**
 * Planifier une pose, d'un seul enregistrement : le technicien, la date prévue, et ce qu'il lira sur place (qui appeler,
 * les consignes). Seul ce qui a changé part au serveur. La même sur la fiche et dans le tiroir de la file.
 */
export default {
  name: 'OrderSchedulingPanel',

  mixins: [CommissioningMixin],

  props: {
    order: { type: Object, required: true },
    // Les techniciens déjà chargés par l'écran ; sinon le panneau les lit lui-même.
    technicians: { type: Array, default: null },
  },

  data() {
    return { form: {}, loadedTechnicians: [], working: false }
  },

  computed: {
    editable() {
      return this.mayFollowOrders && this.order.status === 'open'
    },

    technicianList() {
      return this.technicians || this.loadedTechnicians
    },

    /** Les techniciens, et celui qui a déjà la pose même s'il n'en fait pas partie (prise sur le terrain). */
    technicianChoices() {
      const current = this.order.technician
      return current && !this.technicianList.some(t => t.id === current.id) ? [current, ...this.technicianList] : this.technicianList
    },

    initial() {
      const o = this.order
      return {
        technicianId: o.technician ? o.technician.id : null,
        // Le champ date-heure du navigateur parle en heure locale, sans fuseau.
        scheduledAt: o.scheduledAt ? dayjs(o.scheduledAt).format('YYYY-MM-DDTHH:mm') : '',
        contactName: o.siteContact?.name || '',
        contactPhone: o.siteContact?.phone || '',
        notes: o.installationNotes || '',
      }
    },

    technicianChanged() {
      return (this.form.technicianId || null) !== this.initial.technicianId
    },
    dateChanged() {
      return (this.form.scheduledAt || '') !== this.initial.scheduledAt
    },
    siteChanged() {
      return ['contactName', 'contactPhone', 'notes'].some(key => (this.form[key] || '').trim() !== this.initial[key])
    },
    changed() {
      return this.technicianChanged || this.dateChanged || this.siteChanged
    },
  },

  watch: {
    order: {
      immediate: true,
      handler() {
        this.reset()
      },
    },
  },

  created() {
    if (!this.technicians && this.mayManageOnboarding) {
      CommissioningRepository.technicians().then(list => { this.loadedTechnicians = list }).catch(() => { this.loadedTechnicians = [] })
    }
  },

  methods: {
    reset() {
      this.form = { ...this.initial }
    },

    async save() {
      this.working = true
      const id = this.order.id
      let updated = null
      try {
        if (this.technicianChanged) updated = await CommissioningRepository.assign(id, this.form.technicianId || null)
        if (this.dateChanged) updated = await CommissioningRepository.schedule(id, this.form.scheduledAt ? dayjs(this.form.scheduledAt).format() : null)
        if (this.siteChanged) {
          const contact = { name: (this.form.contactName || '').trim() || null, phone: (this.form.contactPhone || '').trim() || null }
          updated = await CommissioningRepository.changeSite(id, contact, (this.form.notes || '').trim() || null)
        }
        this.notifySuccess('onboarding.commissioning.site.saved')
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.working = false
        // Ce qui est passé avant un refus est enregistré : l'écran le reprend.
        if (updated) this.$emit('updated', updated)
      }
    },
  },
}
</script>
